import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../utils/api';
import Button from './shared/Button';
import Badge from './shared/Badge';
import Alert from './shared/Alert';
import SearchBar from './shared/SearchBar';
import Spinner from './shared/Spinner';

const STORE_LABELS = { qdrant: 'Qdrant', s3: 'S3 Vectors' };
const PROVIDER_LABELS = { sentence_transformer: 'ST', gemini: 'Gemini', openai: 'OpenAI' };

export default function AdminRAGCollections() {
    const navigate = useNavigate();
    const [collections, setCollections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => { loadCollections(); }, []);

    const loadCollections = async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await adminApi.listRAGCollections({ search: searchQuery || undefined });
            setCollections(res.collections || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (e, collectionId, name) => {
        e.stopPropagation();
        if (!confirm(`Delete collection "${name}"? This will remove all documents and vectors.`)) return;
        try {
            await adminApi.deleteRAGCollection(collectionId);
            setSuccessMessage(`Collection "${name}" deleted`);
            loadCollections();
            setTimeout(() => setSuccessMessage(null), 5000);
        } catch (err) {
            setError(err.message);
        }
    };

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900">Manage Collections</h1>
                    <p className="text-sm text-gray-500 mt-0.5">{collections.length} collections</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/admin/rag/settings')}>
                        Settings
                    </Button>
                    <Button size="sm" onClick={() => navigate('/admin/rag/manage/new')}>
                        Create Collection
                    </Button>
                </div>
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}
            {successMessage && <Alert variant="success" onClose={() => setSuccessMessage(null)} className="mb-4">{successMessage}</Alert>}

            <SearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                onSearch={loadCollections}
                onRefresh={loadCollections}
                placeholder="Search collections..."
                className="mb-4"
            />

            {loading ? (
                <Spinner size="lg" text="Loading collections..." center />
            ) : collections.length === 0 ? (
                <div className="text-center py-12 text-gray-500 text-sm">
                    {searchQuery ? 'No collections found matching your search' : 'No collections yet. Create one to get started.'}
                </div>
            ) : (
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50/60">
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Name</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Store</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Embedding</th>
                                <th className="text-center px-3 py-2.5 text-xs font-medium text-gray-500">Docs</th>
                                <th className="text-center px-3 py-2.5 text-xs font-medium text-gray-500">Status</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 w-28">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {collections.map((col) => (
                                <tr
                                    key={col.id}
                                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                                    onClick={() => navigate(`/admin/rag/manage/${col.id}`)}
                                >
                                    <td className="px-4 py-3">
                                        <span className="font-medium text-gray-900">{col.name}</span>
                                        <p className="text-xs text-gray-400 mt-0.5">{col.id}</p>
                                    </td>
                                    <td className="px-3 py-3">
                                        <Badge variant="outline">
                                            {STORE_LABELS[col.vector_store] || col.vector_store}
                                        </Badge>
                                    </td>
                                    <td className="px-3 py-3">
                                        <div className="text-xs text-gray-700">
                                            {PROVIDER_LABELS[col.embedding_provider] || col.embedding_provider}
                                        </div>
                                        <div className="text-xs text-gray-400 truncate max-w-[160px]">
                                            {col.embedding_model}
                                        </div>
                                    </td>
                                    <td className="px-3 py-3 text-center text-gray-700">{col.document_count}</td>
                                    <td className="px-3 py-3 text-center">
                                        <Badge variant={col.is_active ? 'success' : 'default'} dot>
                                            {col.is_active ? 'Active' : 'Inactive'}
                                        </Badge>
                                    </td>
                                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                                        <div className="flex items-center justify-end gap-1">
                                            <Button
                                                variant="ghost"
                                                size="xs"
                                                onClick={() => navigate(`/admin/rag/manage/${col.id}/edit`)}
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="xs"
                                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                onClick={(e) => handleDelete(e, col.id, col.name)}
                                            >
                                                Delete
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
